import { z } from 'zod';
/**
 * JSON-манифест плагина. JSON — декларативное описание, а НЕ исполняемый код:
 * манифест не может содержать скрипт, а backend никогда не выполняет eval
 * (PLUGIN_ARCHITECTURE.md).
 */
/** Версия plugin SDK/контракта, которую понимает это ядро. */
export const PLUGIN_API_VERSION = '1';
/** Allowlist permissions первой версии. Всё, чего здесь нет, запрещено. */
export const PLUGIN_PERMISSIONS = [
    'catalog.read',
    'content.read',
    'search.read',
    'playback.create',
    'ui.menu',
    'ui.home-section',
    'user.favorites.read',
    'user.favorites.write',
    'user.progress.read',
    'user.progress.write',
];
export const pluginPermissionSchema = z.enum(PLUGIN_PERMISSIONS);
/**
 * Явно запрещённые permissions. Проверяются отдельно, чтобы манифест с ними
 * отклонялся с понятной причиной, а не просто «неизвестное значение».
 */
export const FORBIDDEN_PLUGIN_PERMISSIONS = [
    'network.any',
    'storage.raw',
    'tokens.read',
    'cookies.read',
    'eval',
    'filesystem',
];
export const pluginPlatformSchema = z.enum(['web', 'android-tv', 'server']);
const pluginIdSchema = z
    .string()
    .min(3)
    .max(96)
    .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/, 'plugin id must be lowercase reverse-dns');
const semverSchema = z
    .string()
    .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/, 'version must be semver');
/**
 * Entrypoint для web — ВСЕГДА относительный путь нашего домена.
 * Внешние URL запрещены: плагин не загружается со стороннего origin.
 */
const webEntrypointSchema = z
    .string()
    .regex(/^\/plugins\/[A-Za-z0-9._-]+\/[A-Za-z0-9._/-]+\.js$/, 'web entrypoint must be a local /plugins/... path');
export const pluginManifestSchema = z
    .object({
    id: pluginIdSchema,
    name: z.string().min(1).max(120),
    description: z.string().max(500).default(''),
    version: semverSchema,
    apiVersion: z.string().regex(/^\d+$/),
    platforms: z.array(pluginPlatformSchema).min(1),
    permissions: z.array(pluginPermissionSchema).max(PLUGIN_PERMISSIONS.length).default([]),
    entrypoints: z
        .object({
        web: webEntrypointSchema.optional(),
        /** Идентификатор встроенного server provider, а не путь к файлу. */
        server: z
            .string()
            .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/)
            .optional(),
        /** Идентификатор заранее зарегистрированного native extension point. */
        'android-tv': z
            .string()
            .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/)
            .optional(),
    })
        .strict(),
    /** sha256 bundle. Обязателен, когда объявлен web entrypoint. */
    checksum: z
        .string()
        .regex(/^sha256:[a-f0-9]{64}$/)
        .optional(),
    signature: z.string().max(2048).optional(),
})
    .strict()
    .superRefine((manifest, ctx) => {
    if (manifest.platforms.includes('web') && !manifest.entrypoints.web) {
        ctx.addIssue({
            code: 'custom',
            path: ['entrypoints', 'web'],
            message: 'web platform requires a web entrypoint',
        });
    }
    if (manifest.entrypoints.web && !manifest.entrypoints.web.includes(`/plugins/${manifest.id}/`)) {
        ctx.addIssue({
            code: 'custom',
            path: ['entrypoints', 'web'],
            message: 'web entrypoint must live under the plugin own directory',
        });
    }
    if (manifest.entrypoints.web && !manifest.checksum) {
        ctx.addIssue({
            code: 'custom',
            path: ['checksum'],
            message: 'web bundle requires a sha256 checksum',
        });
    }
});
/** То, что реально отдаётся клиенту: без signature и внутренних деталей. */
export const pluginDescriptorSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    version: z.string(),
    apiVersion: z.string(),
    platforms: z.array(pluginPlatformSchema),
    permissions: z.array(pluginPermissionSchema),
    enabled: z.boolean(),
    /** Путь bundle для web-платформы либо null. */
    webEntrypoint: z.string().nullable(),
});
export const pluginListResponseSchema = z.object({
    apiVersion: z.string(),
    plugins: z.array(pluginDescriptorSchema),
    /** Число манифестов, отклонённых валидацией. Причины — только в логах сервера. */
    rejectedCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=plugin.js.map